import type { FC, ReactNode } from 'react'

import Image from 'next/image'
import Link from 'next/link'
import { ArrowRight, Bookmark, CalendarCheck, FileDown, PackageCheck, Ruler, Wallet } from 'lucide-react'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import Reveal from '@/components/generals/reveal'
import { CtaBand, FaqList, ProcessList, SectionHeading } from '@/components/landing/blocks'

const ACCOUNT_FEATURES = [
    { icon: Bookmark, title: 'Ontwerpen bewaren', text: 'Bewaar zoveel ontwerpen als u wilt en vergelijk ze rustig.' },
    { icon: FileDown, title: 'Offertes als PDF', text: 'Met technische tekening, prijsopbouw en voorwaarden.' },
    { icon: Wallet, title: 'Betalingen inzien', text: 'Aanbetaling en restant, altijd met de juiste gegevens.' },
    { icon: CalendarCheck, title: 'Afspraken', text: 'Datum van inmeting en montage op één plek.' },
    { icon: Ruler, title: 'Definitieve maten', text: 'De ingemeten productiematen van uw deur.' },
    { icon: PackageCheck, title: 'Productiestatus', text: 'Van werkplaats tot plaatsing, stap voor stap.' }
]

const WorkflowPage: FC = (): ReactNode => {
    return (
        <Layout>
            <Seo title='Werkwijze' description='Zo werkt ArtDoors: ontwerpen in 3D, offerte of direct bestellen, aanbetaling, inmeten aan huis, productie en montage. Inclusief veelgestelde vragen.' />

            <section id='introductie' className='mx-auto max-w-[1440px] px-5 pt-16 pb-20 sm:px-8 lg:px-12 lg:pt-24'>
                <div className='grid gap-12 lg:grid-cols-[1.2fr_1fr] lg:items-end'>
                    <div>
                        <p className='ad-rise eyebrow'>Werkwijze</p>
                        <h1 className='ad-rise mt-5 font-display text-5xl leading-[1.02] sm:text-7xl' style={{ animationDelay: '80ms' }}>
                            Helder vanaf het eerste ontwerp
                        </h1>
                    </div>

                    <p className='ad-rise text-lg leading-relaxed text-muted-foreground' style={{ animationDelay: '160ms' }}>
                        U ontwerpt uw deur zelf en ziet direct wat hij kost. Daarna regelen wij alles: inmeten, productie en montage. Zonder verrassingen, met een vaste prijs en een persoonlijke klantomgeving.
                    </p>
                </div>
            </section>

            <section id='stappen' className='mx-auto max-w-[1440px] px-5 pb-24 sm:px-8 lg:px-12'>
                <ProcessList />
            </section>

            <section id='inmeten' className='border-y border-border bg-card'>
                <div className='mx-auto grid max-w-[1440px] gap-14 px-5 py-24 sm:px-8 lg:grid-cols-2 lg:items-center lg:px-12 lg:py-32'>
                    <div className='relative aspect-[4/5] overflow-hidden rounded-md'>
                        <Image src='/images/fluted-partition.jpg' alt='Zwart stalen kader met ribbelglas in een licht interieur' fill sizes='(min-width: 1024px) 50vw, 100vw' className='object-cover' />
                    </div>

                    <div>
                        <SectionHeading
                            eyebrow='Inmeten aan huis'
                            title='Tot op de millimeter passend'
                            description='Na uw bestelling en aanbetaling komt onze specialist bij u langs. Hij meet de opening, de muurdikte en de vloer, bespreekt de draairichting en maakt foto’s van de situatie.'
                        />

                        <Reveal delay={100}>
                            <ul className='mt-8 grid gap-4 text-sm'>
                                {[
                                    'Dagmaat en productiemaat worden vastgelegd in uw dossier',
                                    'Bijzonderheden zoals een nog te leggen vloer nemen we mee',
                                    'Na de inmeting gaat uw deur direct naar de fabriek'
                                ].map(item => (
                                    <li key={item} className='flex gap-3 border-t border-foreground/10 pt-4'>
                                        <Ruler className='mt-0.5 size-4 shrink-0 text-brass-strong' aria-hidden='true' />
                                        {item}
                                    </li>
                                ))}
                            </ul>
                        </Reveal>
                    </div>
                </div>
            </section>

            <section id='klantomgeving' className='mx-auto max-w-[1440px] px-5 py-24 sm:px-8 lg:px-12 lg:py-32'>
                <SectionHeading
                    eyebrow='Mijn ArtDoors'
                    title='Alles over uw deur, altijd bij de hand'
                    description='In uw persoonlijke klantomgeving volgt u uw deur van ontwerp tot plaatsing. Wij werken de status bij zodra er iets verandert.'
                />

                <div className='mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-3'>
                    {ACCOUNT_FEATURES.map((feature, index) => {
                        const Icon = feature.icon

                        return (
                            <Reveal key={feature.title} delay={index * 60} className='rounded-md bg-card p-6 ring-1 ring-border'>
                                <Icon className='size-6 text-brass-strong' aria-hidden='true' />
                                <h3 className='mt-4 text-base font-semibold'>{feature.title}</h3>
                                <p className='mt-1.5 text-sm leading-relaxed text-muted-foreground'>{feature.text}</p>
                            </Reveal>
                        )
                    })}
                </div>

                <Reveal delay={100}>
                    <Link href='/account/registreren' className='mt-10 inline-flex h-12 items-center gap-2 rounded-sm bg-primary px-6 text-sm font-semibold text-primary-foreground'>
                        Maak een gratis account
                        <ArrowRight className='size-4' aria-hidden='true' />
                    </Link>
                </Reveal>
            </section>

            <section id='veelgestelde-vragen' className='scroll-mt-24 bg-card'>
                <div className='mx-auto grid max-w-[1440px] gap-12 px-5 py-24 sm:px-8 lg:grid-cols-[0.8fr_1.2fr] lg:px-12 lg:py-32'>
                    <SectionHeading eyebrow='Veelgestelde vragen' title='Antwoord op uw vragen' description='Staat uw vraag er niet bij? Stuur ons een bericht, we denken graag met u mee.' />

                    <Reveal delay={80}>
                        <FaqList />
                        <Link href='/contact' className='mt-6 inline-flex items-center gap-2 text-sm font-semibold'>
                            Stel uw vraag
                            <ArrowRight className='size-4' aria-hidden='true' />
                        </Link>
                    </Reveal>
                </div>
            </section>

            <CtaBand />
        </Layout>
    )
}

export default WorkflowPage
